import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactPaginate from 'react-paginate';

import IconAddlistOrange from '../../assets/images/common/icon-addlist-orange.svg';
import IconInfoOrange from '../../assets/images/common/icon-info-orange.svg';
import PagerLeft from '../../assets/images/common/pager-left.svg';
import PagerLeftGrey from '../../assets/images/common/pager-left_grey.svg';
import PagerRight from '../../assets/images/common/pager-right.svg';
import PagerRightGrey from '../../assets/images/common/pager-right_grey.svg';

import { ListAddMessage } from '../../types/common/SearchResultType';
import { MAX_SEARCH_RESULT_SHOW_COUNT } from '../../types/common/CommonType';
import { ErrorMessages } from '../../types/common/Messages';
import { SearchApiResponse, SearchItems, SEARCH_ITEMS_INIT } from '../../types/api/SearchApiType';
import { ChangeFieldItemInput, DeviceInfoRow, CHANGE_FIELD_ITEM_DEFAULT } from '../../types/common/EstimateInquiryType';

import { SearchResultRow } from './SearchResultRow';
import { SearchResultNotFound } from './SearchResultNotFound';
import { AddDeviceModal } from './AddDeviceModal';
import { SearchResultBottom } from './SearchResultBottom';
import { SearchResultLogic } from './searchResultLogic';
import { SearchTextField } from '../../components/SearchTextField';
import { ErrorModalModal } from '../../components/ErrorModal';
import { ErrorMessage } from '../../components/ErrorMessage';
import { AppDispatch } from '../../store/store';
import { useAppSelector } from '../../store/hooks';
import { setState as setSearchResultOffset } from '../../slice/SearchResultOffset';
import { moveToPageTop } from '../../utils/pageMoveUtil';

type SearchResultBodyProps = {
    searchResult: SearchApiResponse;
    searchWord: string;
    deviceList: Array<DeviceInfoRow>;
    dispatch: AppDispatch;
};

export const SearchResultBody: React.FC<SearchResultBodyProps> = ({
    searchResult,
    searchWord,
    deviceList,
    dispatch,
}) => {
    const navigation = useNavigate();
    // 検索結果のページングオフセット位置
    const offsetSlice = useAppSelector((s) => s.SearchResultOffset);

    const [addMessage, setAddMessage] = useState<string>('');
    // エラー モーダル開閉管理
    const [openErrorlModal, setOpenErrorModal] = useState<boolean>(false);
    const [errorMessages, setErrorMessages] = useState<Array<string>>([]);
    // ページネーションオフセット
    const [pageOffset, setPageOffset] = useState<number>(offsetSlice);
    // 機器リスト追加モーダル用
    const [addDeviceModal, setAddDeviceModal] = useState<boolean>(false);
    // 機器リスト追加モーダルで保持すべき選択行の内容
    const [searchItems, setSearchItems] = useState<SearchItems>(SEARCH_ITEMS_INIT);
    // 機器リスト追加モーダルにて選択された内容
    const [modalItems, setModalItems] = useState<ChangeFieldItemInput>(CHANGE_FIELD_ITEM_DEFAULT);
    // 機器追加結果のメッセージリスト
    const [listAddMessage, setListAddMessage] = useState<ListAddMessage>({
        mp_id: 0,
        addMessage: '',
    });

    // 再描画時の初期化
    useEffect(() => {
        setAddMessage('');
        setErrorMessages([]);
        setPageOffset(offsetSlice);
    }, [searchResult, offsetSlice]);

    // ページネーションで数値クリック時のアクション
    const moveResultNumberPage = (selectNumber: number) => {
        setErrorMessages([]);
        setPageOffset(selectNumber * MAX_SEARCH_RESULT_SHOW_COUNT);
        // 検索オフセットを記憶
        dispatch(setSearchResultOffset(selectNumber * MAX_SEARCH_RESULT_SHOW_COUNT));
        // ぺージの先頭に移動
        moveToPageTop();
    };

    // 左右のページネーション移動アイコンクリック時のアクション
    const moveResultLeftRight = (nexpage: number) => {
        setErrorMessages([]);
        setPageOffset(pageOffset + nexpage * MAX_SEARCH_RESULT_SHOW_COUNT);
        // 検索オフセットを記憶
        dispatch(setSearchResultOffset(pageOffset + nexpage * MAX_SEARCH_RESULT_SHOW_COUNT));
        // ぺージの先頭に移動
        moveToPageTop();
    };

    // 検索結果の機器リストへの追加(モーダルにて実行)
    const addSearchResultDeviceList = () => {
        void SearchResultLogic.addSearchResultDeviceList(
            deviceList,
            searchItems,
            modalItems,
            setListAddMessage,
            setErrorMessages,
            setOpenErrorModal,
            setAddDeviceModal,
            dispatch
        );
    };

    // 検索キーワードの機器リストへの追加
    const searchWordDeviceListAdd = (searchText: string) => {
        return SearchResultLogic.searchWordDeviceListAdd(
            searchText,
            deviceList,
            setAddMessage,
            setErrorMessages,
            setOpenErrorModal,
            dispatch
        );
    };

    const moveEditDevice = () => {
        navigation('/editDeviceList');
    };

    const bottomCtaElement = useRef<HTMLElement | null>(null);
    const bottomCtaFloatElement = useRef<HTMLElement | null>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (null === bottomCtaFloatElement || !bottomCtaFloatElement.current) {
                return;
            }
            if (entry.isIntersecting) {
                // 「見積り機器編集」ボタンが見える場合、floatボタンを隠す
                bottomCtaFloatElement.current.style.display = 'none';
            } else {
                // 見えない場合、float領域を表示
                bottomCtaFloatElement.current.style.display = 'block';
            }
        });
        if (observer && bottomCtaElement.current) {
            // 下のボタンが見えるかどうかを監視する
            observer.observe(bottomCtaElement.current);
        }
    }, []);

    return (
        <>
            {searchResult.count !== 0 ? (
                <>
                    <section className='section page_title'>
                        <div className='container'>
                            <div className='columns is-desktop'>
                                <div className='column is-2-desktop is-12-mobile page_title-thead'>
                                    <h1>検索結果</h1>
                                </div>
                                <div className='column is-10-desktop is-12-mobile page_title-search_box--wrapper'>
                                    <p className='discription'>
                                        機種名称を再検索したい方は
                                        <br className='is-hidden-mobile' />
                                        こちらから再入力・再検索を行ってください。
                                    </p>
                                    <SearchTextField inputClassName='input is-top_search' buttonClassName='button' />
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className='section content-wrapper'>
                        <div className='container'>
                            <div className='columns search_results--header'>
                                <div className='column is-8'>
                                    <div className='search_results'>
                                        <h2 className='search_results--title'>
                                            {searchWord && (
                                                <>
                                                    <span>{searchWord}</span>
                                                    に関する機種が
                                                </>
                                            )}
                                            <strong>{searchResult.count}件</strong>
                                            見つかりました。
                                        </h2>
                                    </div>
                                </div>
                                <div className='column is-4 add_keyword'>
                                    <button
                                        className='button is-small search_results--add_keyword'
                                        onClick={() => searchWordDeviceListAdd(searchWord)}
                                    >
                                        <span>検索キーワードを見積り機器に追加</span>
                                        <span className='icon is-small is-right'>
                                            <img src={IconAddlistOrange} alt='' />
                                        </span>
                                    </button>
                                    {addMessage && <div>{addMessage}</div>}
                                </div>
                            </div>
                            <div className='columns search_results--text'>
                                <div className='column'>
                                    <p className='para-small'>
                                        お客様の見積り機器に追加することで概算見積依頼に利用できます。
                                    </p>
                                </div>
                                <div className='column'>
                                    <div className='alert_box'>
                                        <span className='icon'>
                                            <img src={IconInfoOrange} alt='' />
                                        </span>
                                        <span className='text'>見積り機器に追加できるのは10種類までです。</span>
                                    </div>
                                </div>
                            </div>
                            {errorMessages.length > 0 &&
                                errorMessages.map((message, index) => (
                                    <div className='columns search_results--text' key={`${message}-${index}`}>
                                        <div className='column'>
                                            <ErrorMessage errorMessage={message} />
                                        </div>
                                    </div>
                                ))}
                            <div className='columns search_results--list'>
                                <div className='column'>
                                    {/* 検索結果 */}
                                    {searchResult.items
                                        .slice(pageOffset, pageOffset + MAX_SEARCH_RESULT_SHOW_COUNT)
                                        .map((row, index) => (
                                            <React.Fragment key={`${row.mp_id}-${index}`}>
                                                <SearchResultRow
                                                    searchResultRow={row}
                                                    searchWord={searchWord}
                                                    setAddDeviceModal={setAddDeviceModal}
                                                    deviceListCount={deviceList.length}
                                                    setSearchItems={setSearchItems}
                                                    addMessage={
                                                        listAddMessage.mp_id === row.mp_id
                                                            ? listAddMessage.addMessage
                                                            : ''
                                                    }
                                                    setErrorMessages={setErrorMessages}
                                                    setOpenErrorModal={setOpenErrorModal}
                                                />
                                            </React.Fragment>
                                        ))}
                                    {/* ページネーション デザインの都合上、別途定義 */}
                                    <nav className='pagination is-centered' role='navigation' aria-label='pagination'>
                                        {Math.ceil(pageOffset / MAX_SEARCH_RESULT_SHOW_COUNT) === 0 ? (
                                            <a className='pagination-previous'>
                                                <img src={PagerLeftGrey} />
                                            </a>
                                        ) : (
                                            <a className='pagination-previous' onClick={() => moveResultLeftRight(-1)}>
                                                <img src={PagerLeft} />
                                            </a>
                                        )}
                                        {Math.ceil(pageOffset / MAX_SEARCH_RESULT_SHOW_COUNT) ===
                                            Math.ceil(searchResult.count / MAX_SEARCH_RESULT_SHOW_COUNT - 1) ? (
                                            <a className='pagination-next'>
                                                <img src={PagerRightGrey} />
                                            </a>
                                        ) : (
                                            <a className='pagination-next' onClick={() => moveResultLeftRight(1)}>
                                                <img src={PagerRight} />
                                            </a>
                                        )}
                                        <ReactPaginate
                                            className={'pagination-list'}
                                            pageClassName={'pagination-link'}
                                            previousLabel={''}
                                            nextLabel={''}
                                            breakLabel={<span className='pagination-ellipsis'>&hellip;</span>}
                                            pageCount={Math.ceil(searchResult.count / MAX_SEARCH_RESULT_SHOW_COUNT)}
                                            onPageChange={(e) => moveResultNumberPage(e.selected)}
                                            marginPagesDisplayed={1}
                                            pageRangeDisplayed={2}
                                            activeClassName={'is-current'}
                                            activeLinkClassName={'is-current-link'}
                                            forcePage={Math.ceil(pageOffset / MAX_SEARCH_RESULT_SHOW_COUNT)}
                                        />
                                    </nav>
                                </div>
                            </div>
                        </div>
                    </section>
                    <section ref={bottomCtaElement} className='section bottom_cta'>
                        <SearchResultBottom moveEditDevice={moveEditDevice} />
                    </section>
                    <section ref={bottomCtaFloatElement} className='section bottom_cta bottom_cta_float'>
                        <SearchResultBottom moveEditDevice={moveEditDevice} />
                    </section>
                </>
            ) : (
                <SearchResultNotFound
                    searchWord={searchWord}
                    searchWordDeviceListAdd={searchWordDeviceListAdd}
                    errorMessages={errorMessages}
                />
            )}
            <AddDeviceModal
                openState={addDeviceModal}
                setOpenState={setAddDeviceModal}
                modalItems={modalItems}
                setModalItems={setModalItems}
                addButtonFunc={addSearchResultDeviceList}
            />
            <ErrorModalModal
                openState={openErrorlModal}
                setOpenState={setOpenErrorModal}
                errorMessages={[ErrorMessages.DEVICE_LIST_ADD_ERROR]}
                contactShow
                contactShowErrorMessage={ErrorMessages.DEVICE_LIST_ERROR_CONTACT}
            />
        </>
    );
};
